// Multilingual PDF export — uses html2canvas to render the diagnosis result panel
// (preserving Hindi/Marathi/Tamil/Telugu glyphs) and embeds it in jsPDF.
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export const exportScanToPDF = async (scan, panelEl) => {
  const r = scan?.result || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 32;

  // Header bar (Latin-only)
  doc.setFillColor(27, 67, 50);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(212, 163, 115);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Hansa — Diagnosis Report", margin, 36);

  doc.setTextColor(28, 28, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(82, 121, 111);
  doc.text(
    `Generated: ${new Date().toLocaleString()}  •  Confidence: ${r.confidence || 0}%`,
    margin,
    74
  );

  let y = 90;

  // Crop image
  try {
    if (scan.image_base64?.startsWith("data:image")) {
      const fmt = scan.image_base64.includes("png") ? "PNG" : "JPEG";
      const imgW = 220;
      const imgH = 165;
      doc.addImage(scan.image_base64, fmt, margin, y, imgW, imgH, undefined, "FAST");
      y += imgH + 14;
    }
  } catch (err) {
    // Header image is optional — log but don't fail the whole export.
    if (process.env.NODE_ENV !== "production") console.warn("pdf: header image skipped", err);
  }

  // Render the result panel as image so non-Latin scripts work
  if (panelEl) {
    try {
      const canvas = await html2canvas(panelEl, {
        backgroundColor: "#FDFBF7",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const ratio = canvas.width / canvas.height;
      const targetW = pageW - 2 * margin;
      const targetH = targetW / ratio;
      const remaining = pageH - y - margin;
      if (targetH <= remaining) {
        doc.addImage(imgData, "JPEG", margin, y, targetW, targetH, undefined, "FAST");
      } else {
        // Paginate: break the canvas into page-sized slices
        const sliceCanvasH = (canvas.width * remaining) / targetW;
        let yOffset = 0;
        let firstPage = true;
        while (yOffset < canvas.height) {
          const h = Math.min(sliceCanvasH, canvas.height - yOffset);
          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = h;
          const ctx = slice.getContext("2d");
          ctx.drawImage(canvas, 0, yOffset, canvas.width, h, 0, 0, canvas.width, h);
          const sliceData = slice.toDataURL("image/jpeg", 0.85);
          const sliceTargetH = (h * targetW) / canvas.width;
          if (!firstPage) {
            doc.addPage();
            y = margin;
          }
          doc.addImage(sliceData, "JPEG", margin, y, targetW, sliceTargetH, undefined, "FAST");
          yOffset += h;
          firstPage = false;
        }
      }
    } catch (e) {
      console.error("PDF panel render failed", e);
      // Fallback: write summary as Latin-1 text (may show boxes for non-Latin)
      doc.setFontSize(11);
      doc.setTextColor(28, 28, 28);
      const lines = doc.splitTextToSize(
        r.summary || r.disease_name || r.crop_name || "Diagnosis",
        pageW - 2 * margin
      );
      lines.forEach((ln) => {
        if (y > pageH - 60) {
          doc.addPage();
          y = margin;
        }
        doc.text(ln, margin, y);
        y += 14;
      });
    }
  }

  // Footer on last page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(82, 121, 111);
    doc.text(
      "Generated by Hansa • Powered by Ramkishan Agri Innovate • Always confirm chemical dosages with local agronomy experts.",
      margin,
      pageH - 18
    );
    doc.text(`${i} / ${pageCount}`, pageW - margin - 30, pageH - 18);
  }

  const filename = `hansa-${(r.disease_name || r.crop_name || "scan")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 30)}.pdf`;
  doc.save(filename);
};
